/**
 * Utilitário centralizado para cores e gradientes do tema White Label
 * 
 * Este arquivo centraliza a lógica de aplicação de cores e gradientes,
 * garantindo consistência visual em toda a aplicação.
 */

/**
 * Gera um gradiente linear entre cor primária e secundária
 * @param {string} primaryColor - Cor primária (hex)
 * @param {string} secondaryColor - Cor secundária (hex)
 * @param {number} angle - Ângulo do gradiente (default: 135)
 * @returns {string} CSS gradient string
 */
export const createGradient = (primaryColor, secondaryColor, angle = 135) => {
  const secondary = secondaryColor || primaryColor;
  return `linear-gradient(${angle}deg, ${primaryColor} 0%, ${secondary} 100%)`;
};

/**
 * Retorna background sólido ou gradiente baseado na configuração
 * @param {object} options - Opções de configuração
 * @param {string} options.primaryColor - Cor primária
 * @param {string} options.secondaryColor - Cor secundária (opcional)
 * @param {boolean} options.useGradient - Se deve usar gradiente
 * @param {number} options.angle - Ângulo do gradiente (default: 135)
 * @returns {string} CSS background value
 */
export const getBackground = ({ primaryColor, secondaryColor, useGradient, angle = 135 }) => {
  if (useGradient && secondaryColor) {
    return createGradient(primaryColor, secondaryColor, angle);
  }
  return primaryColor;
};

/**
 * Retorna estilos de background para uso em makeStyles ou style inline
 * @param {object} options - Opções de configuração
 * @param {string} options.primaryColor - Cor primária
 * @param {string} options.secondaryColor - Cor secundária (opcional)
 * @param {boolean} options.useGradient - Se deve usar gradiente
 * @param {number} options.angle - Ângulo do gradiente (default: 135)
 * @returns {object} Objeto com propriedade background
 */
export const getBackgroundStyle = (options) => ({
  background: getBackground(options)
});

/**
 * Hook helper para obter cores do tema a partir do contexto
 * Deve ser usado dentro de componentes React
 * @param {object} theme - Tema do Material-UI
 * @param {object} colorModeContext - Contexto de cores (opcional)
 * @returns {object} Objeto com cores e função de gradiente
 */
export const getThemeColors = (theme, colorModeContext = {}) => {
  const isDark = theme.palette.type === 'dark';
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary?.main || primaryColor;
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;

  return {
    primaryColor,
    secondaryColor,
    useGradient,
    isDark,
    // Função para obter background
    getBackground: (angle = 135) => getBackground({
      primaryColor,
      secondaryColor,
      useGradient,
      angle
    }),
    // Função para obter estilo de background
    getBackgroundStyle: (angle = 135) => getBackgroundStyle({
      primaryColor,
      secondaryColor,
      useGradient,
      angle
    }),
    // Gradiente forçado (ignora configuração useGradient)
    forceGradient: (angle = 135) => createGradient(primaryColor, secondaryColor, angle),
    // Cor sólida forçada (ignora configuração useGradient)
    forceSolid: () => primaryColor
  };
};

/**
 * Gera classes CSS para uso com makeStyles
 * Retorna classes condicionais para gradiente ou sólido
 * @param {object} theme - Tema do Material-UI
 * @returns {object} Objeto com estilos para gradiente e sólido
 */
export const createGradientStyles = (theme) => {
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary?.main || primaryColor;

  return {
    // Classe para background com gradiente
    gradientBackground: {
      background: createGradient(primaryColor, secondaryColor),
      color: theme.palette.primary.contrastText || '#fff',
    },
    // Classe para background sólido
    solidBackground: {
      backgroundColor: primaryColor,
      color: theme.palette.primary.contrastText || '#fff',
    },
    // Classe para texto com gradiente (efeito especial)
    gradientText: {
      background: createGradient(primaryColor, secondaryColor),
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    // Classe para borda com gradiente
    gradientBorder: {
      border: 'none',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        padding: '2px',
        background: createGradient(primaryColor, secondaryColor),
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
      }
    },
    // Botão com gradiente e hover
    gradientButton: {
      background: createGradient(primaryColor, secondaryColor),
      color: '#fff',
      transition: 'all 0.3s ease',
      '&:hover': {
        filter: 'brightness(1.1)',
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 12px ${primaryColor}40`,
      },
      '&:disabled': {
        background: theme.palette.type === 'dark' ? '#555' : '#ccc',
        color: theme.palette.type === 'dark' ? '#888' : '#999',
        filter: 'none',
        transform: 'none',
        boxShadow: 'none',
      }
    },
    // Botão sólido com hover
    solidButton: {
      backgroundColor: primaryColor,
      color: '#fff',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark || primaryColor,
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 12px ${primaryColor}40`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.type === 'dark' ? '#555' : '#ccc',
        color: theme.palette.type === 'dark' ? '#888' : '#999',
        transform: 'none',
        boxShadow: 'none',
      }
    }
  };
};

/**
 * Cores padrão do sistema (fallbacks)
 */
export const DEFAULT_COLORS = {
  primaryLight: '#0000FF',
  primaryDark: '#065183',      // Azul corporativo (original do sistema)
  secondaryLight: '#764ba2',
  secondaryDark: '#0d47a1',    // Azul escuro (harmoniza melhor no dark mode)
};

export default {
  createGradient,
  getBackground,
  getBackgroundStyle,
  getThemeColors,
  createGradientStyles,
  DEFAULT_COLORS,
};
